import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from 'constants/ApiConstant';
import { AUTH_TOKEN } from 'constants/AuthConstant';

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  redirect: '',
  users: [],
  currentUser: null,
  analytics: {},
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  }
};

//get analytics
export const getAnalytics = createAsyncThunk(
  'user/getAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}admin/analytics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
        }
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error fetching analytics';
      return rejectWithValue(errorMessage);
    }
  }
)
// Create User
export const createUser = createAsyncThunk(
  'user/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}admin/createUser`, userData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
        }
      });
      return response.data.user;
    } catch (err) {
      const backendError = err.response?.data?.error;
      const fallbackError = err.message || 'Error creating user';
      const errorMessage = backendError || fallbackError;
      return rejectWithValue(errorMessage);
    }
  }
);

// Update User
export const updateUser = createAsyncThunk(
  'user/updateUser',
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      console.clear()
      console.log(userData)
      const response = await axios.put(`${API_BASE_URL}admin/editSingleUser/${id}`, userData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
        }
      });
      return response.data.user;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error updating user';
      return rejectWithValue(errorMessage);
    }
  }
);

// Get Single User
export const getUser = createAsyncThunk(
  'user/getUser',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}admin/getSingleUser/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
        }
      });

      return response.data.user;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error fetching user';
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete User
export const deleteUser = createAsyncThunk(
  'user/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}admin/deleteSingleUser/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
        }
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error deleting user';
      return rejectWithValue(errorMessage);
    }
  }
);

// List Users
export const listUsers = createAsyncThunk(
  'user/listUsers',
  async (params = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}admin/getAllUsers`, {
        // params,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
        }
      });
      return response.data.users;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error fetching users';
      return rejectWithValue(errorMessage);
    }
  }
);

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    showUserMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideUserMessage: (state) => {
      state.message = '';
      state.showMessage = false;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
    showLoading: (state) => {
      state.loading = true;
    }
  },
  extraReducers: (builder) => {
    builder
      // get analytics
      .addCase(getAnalytics.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload;
      })
      .addCase(getAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })

      // Create User
      .addCase(createUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.showMessage = true;
        state.users.unshift(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })

      // Update User
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.showMessage = true;
        state.users = state.users.map(user =>
          user._id === action.payload._id ? action.payload : user
        );
        if (state.currentUser?._id === action.payload._id) {
          state.currentUser = action.payload.data;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })

      // Get User
      .addCase(getUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })

      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.showMessage = true;
        state.users = state.users.filter(user => user._id !== action.payload.id);
        if (state.currentUser?._id === action.payload.id) {
          state.currentUser = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })

      // List Users
      .addCase(listUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(listUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
        state.pagination = action.payload.pagination;
      })
      .addCase(listUsers.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      });
  }
});

export const {
  showUserMessage,
  hideUserMessage,
  setCurrentUser,
  clearCurrentUser,
  showLoading
} = userSlice.actions;

export default userSlice.reducer;